<template>
  <form autocomplete="on" @submit.prevent="registrarLoguearUsuario()">
    <div class="text-primary mb-2 introduce-msg">
      <span>{{ $t('home.form.personalInformation.dataRegisterGoogle') }}</span>
    </div>

    <template v-for="(campo, i) in campos" :key="campo">
      <div class="mb-2">
        <template v-if="campo === 'email'">
          <label for="email" class="form-label text-primary small">
            {{ $t('home.form.personalInformation.email') }}
          </label>
          <input
            id="exampleFormControlInput1"
            v-model="usuarioRegistroForm.email"
            type="email"
            class="text-primary form-control text-start p text-12"
            :placeholder="'*' + $t('home.form.personalInformation.email')"
            :disabled="props.informacionRegistroGoogle.email != null"
            required />
        </template>

        <template v-else-if="campo === 'nombre'">
          <label for="nombre" class="form-label text-primary small">
            {{ $t('home.form.personalInformation.name') }}
          </label>
          <input
            id="exampleFormControlInput1"
            v-model="usuarioRegistroForm.nombre"
            type="text"
            class="text-primary form-control text-start text-12"
            :placeholder="'*' + $t('home.form.personalInformation.name')"
            :disabled="props.informacionRegistroGoogle.nombre != null"
            required />
        </template>

        <template v-else-if="campo === 'apellidos'">
          <label for="apellidos" class="form-label text-primary small">
            {{ $t('home.form.personalInformation.lastName') }}
          </label>
          <input
            id="exampleFormControlInput1"
            v-model="usuarioRegistroForm.apellidos"
            type="text"
            class="text-primary form-control text-start text-12"
            :placeholder="'*' + $t('home.form.personalInformation.lastName')"
            :disabled="props.informacionRegistroGoogle.apellidos != null"
            required />
        </template>

        <template v-else-if="campo === 'fechaNacimiento'">
          <label for="fechaNacimiento" class="form-label text-primary small">
            {{ $t('home.form.personalInformation.birthDate') }}
          </label>
          <Datepicker
            v-model="usuarioRegistroForm.fechaNacimiento"
            :locale="locale"
            auto-apply
            required
            :placeholder="'*' + $t('home.form.personalInformation.birthDate')"
            :format="format"
            :flow="flowCalendar"
            :disabled="props.informacionRegistroGoogle.fechaNacimiento != null" />
        </template>

        <template v-else-if="campo === 'telefono'">
          <label for="telefono" class="form-label text-primary small">
            {{ $t('home.form.personalInformation.phone') }}
          </label>
          <InputTelefono
            v-model="usuarioRegistroForm.telefono"
            :disabled="props.informacionRegistroGoogle.telefono != null"
            :placeholder="'*' + $t('home.form.personalInformation.phone')"
            required />
        </template>
      </div>

      <div v-if="i == lastPosRelleno" class="text-primary mb-2 introduce-msg text-lg-center">
        <span>{{ $t('home.form.personalInformation.dataFromGoogle') }}</span>
      </div>
    </template>

    <div class="w-100 m-auto">
      <div class="col-12">
        <div class="form-check border-0 d-flex align-items-center mb-2">
          <input
            id="aceptarMailing"
            v-model="usuarioRegistroForm.mailingPublicidadActivo"
            class="form-check-input"
            type="checkbox" />
          <label class="form-check-label text-start ms-2 mt-1" for="aceptarMailing">
            <span class="text-12">{{ $t('home.form.personalInformation.noRecibirMailing') }}</span>
          </label>
        </div>
        <div class="form-check border-0 d-flex align-items-center mb-2">
          <input id="flexCheckChecked" class="form-check-input" type="checkbox" value="" required />
          <label class="form-check-label text-start ms-2 mt-1" for="flexCheckChecked">
            <span class="text-12">{{ $t('home.form.policy.readAcceptsLos') }}</span>
            <a class="btn btn-link p-0 ms-1" @click="openInTab('TerminosCondiciones')">
              <span class="text-12">{{ $t('home.form.policy.terminosCondiciones') }}</span>
            </a>
            <span class="text-12">{{ $t('home.form.policy.andLa') }}</span>
            <a class="btn btn-link p-0" @click="openInTab('PoliticaPrivacidad')">
              <span class="text-12">{{ $t('home.form.policy.politicaPrivacidad') }}.</span>
            </a>
          </label>
        </div>
      </div>
    </div>

    <button type="submit" class="btn btn-primary mb-2 mx-auto w-100 text-submit">
      <span class="text-mobile">{{ $t('botones.aceptarYContinuar') }}</span>
    </button>
  </form>
</template>

<script setup>
  import { alertAutoCloseWarningWithoutCallback } from '@/services/utils/AlertUtils';
  import { reactive, ref, computed } from 'vue';
  import { createToaster } from '@meforma/vue-toaster';
  import Datepicker from '@vuepic/vue-datepicker';
  import UsuarioApiService from '@/services/api/UsuarioApiService';
  import { controlErrorRegistrarLoginUsuario } from '@/services/utils/ErrorControl';
  import router from '@/router';
  import { useI18n } from 'vue-i18n';
  import { validateEmailFormat } from '@/helpers/stringUtiles';
  import { useUserStore } from '@/store_pinia/userStore';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import InputTelefono from '@/components/inputs/InputTelefono.vue';

  const emits = defineEmits(['usuarioLogueado']);

  const props = defineProps({
    informacionRegistroGoogle: {
      type: Object,
      required: true,
      default() {
        return {};
      },
    },
  });

  const { t, locale } = useI18n();

  const usuarioRegistroForm = reactive({
    nombre: props.informacionRegistroGoogle.nombre,
    apellidos: props.informacionRegistroGoogle.apellidos,
    telefono: props.informacionRegistroGoogle.telefono,
    email: props.informacionRegistroGoogle.email,
    fechaNacimiento: props.informacionRegistroGoogle.fechaNacimiento,
    mailingPublicidadActivo: false,
  });

  const camposName = ['email', 'nombre', 'apellidos', 'fechaNacimiento', 'telefono'];

  const camposRellenos = computed(() =>
    camposName.filter(campo => {
      return props.informacionRegistroGoogle[campo] != null;
    })
  );

  const camposVacios = computed(() =>
    camposName.filter(campo => {
      return props.informacionRegistroGoogle[campo] == null;
    })
  );

  const lastPosRelleno = computed(() => camposRellenos.value.length - 1);
  const campos = computed(() => {
    return [...camposRellenos.value, ...camposVacios.value];
  });

  const toaster = createToaster({ position: 'top-right' });
  const flowCalendar = ref(['year', 'month', 'calendar']);
  const format = date => {
    return date.toISOString().split('T')[0];
  };
  const userStore = useUserStore();
  const loadingStore = useLoadingStore();

  const openInTab = routeName => {
    window.open(router.resolve({ name: routeName }).href, '_blank');
  };

  async function registrarLoguearUsuario() {
    try {
      loadingStore.show();

      if (!validateEmailFormat(usuarioRegistroForm.email)) {
        toaster.error(t('home.form.personalInformation.errorEmailFormal'));
        return;
      }

      if (usuarioRegistroForm.nombre === null) {
        toaster.error('home.form.personalInformation.errorNombre');
        return;
      }

      if (usuarioRegistroForm.apellidos === null) {
        toaster.error('home.form.personalInformation.errorApellidos');
        return;
      }

      if (usuarioRegistroForm.fechaNacimiento === null) {
        toaster.error('home.form.personalInformation.errorFechaNacimiento');
        return;
      }

      if (usuarioRegistroForm.telefono === null) {
        toaster.error('home.form.personalInformation.errorTelefono');
        return;
      }

      let request = {
        ...usuarioRegistroForm,
      };

      if (usuarioRegistroForm.fechaNacimiento instanceof Date) {
        request.fechaNacimiento = format(usuarioRegistroForm.fechaNacimiento);
      }

      if (usuarioRegistroForm.telefono) {
        request.telefono = request.telefono.replace(/\s+/g, '');
      }

      request.mailingPublicidadActivo = !request.mailingPublicidadActivo;

      let res = await UsuarioApiService.registrarYLoguearGoogle(request);
      if (res.token == null) {
        alertAutoCloseWarningWithoutCallback(3000, t('errors.inesperado'));
      } else {
        userStore.login(res.token);
        emits('usuarioLogueado');
        toaster.success(t('home.form.personalInformation.registradoCorrect'));
      }
    } catch (error) {
      console.error(error);
      controlErrorRegistrarLoginUsuario(error);
    } finally {
      loadingStore.hide();
    }
  }
</script>

<style lang="scss" scoped>
  .cambio-vista {
    color: var(--bs-primary);
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    transition: 0.3s ease-in-out;

    &:hover {
      color: var(--bs-secondary);
    }
  }

  .position-relative .toggle-password {
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    z-index: 2;
  }

  .password-validation p {
    font-size: 11px;
  }

  .red-text {
    color: var(--rojo-500);
  }

  .green-text {
    color: var(--verde-500);
  }

  .unselectable {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  :deep(.dp__input) {
    text-align: start !important;
    @media only screen and (max-width: 768px) {
      font-size: 12px !important;
    }
  }

  .introduce-msg {
    font-size: 14px;
  }

  .text-submit {
    font-weight: 600;
    box-shadow: 0px 1px 2px 0px rgba(var(--bs-primary-rgb), 0.35) !important;
  }

  .text-12 {
    @media only screen and (max-width: 768px) {
      font-size: 12px !important;
    }
  }

  .mostrar {
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }

  .text-mobile {
    @media only screen and (max-width: 768px) {
      font-size: 14px !important;
      font-weight: 600 !important;
    }
  }

  .btn-link {
    margin-bottom: 1px;
  }
</style>
